import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import { WebuildDatahub } from "@webuild-ai/data-hub";
import React, { createContext, useContext, useMemo, useEffect, useState } from "react";
import { EventSource } from "eventsource";
import { useAuth, getAuthConfig } from "@webuild-ai/react-auth";

import { siteConfig } from "@/config/site";
import { dispatchIdpSessionExpired } from "@/types/sessionEvents";
import { logger } from "@/telemetry";

/**
 * Authenticated fetch function type for making API requests with auth headers.
 * Use this for fetching temporary download URLs or any API endpoint that requires authentication.
 */
export type AuthenticatedFetchFn = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1],
) => ReturnType<typeof fetch>;

const WebuildAPIClientContext = createContext<{
  platformClient: WebuildAiPlatform | null;
  adminClient: WeBuildAiAdmin | null;
  dataHubClient: WebuildDatahub | null;
  sseWorkflowClient: (workflowId: string) => EventSource | null;
  /**
   * Authenticated fetch function that automatically includes the Bearer token.
   * Use this for fetching temporary download URLs or any API endpoint that requires authentication.
   */
  authenticatedFetch: AuthenticatedFetchFn | null;
}>({
  platformClient: null,
  adminClient: null,
  dataHubClient: null,
  sseWorkflowClient: () => null,
  authenticatedFetch: null,
});

export const WeBuildClientProvider = ({ children }: { children: React.ReactNode }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [, setTokenError] = useState<string | null>(null);
  const [idpExpiredDispatched, setIdpExpiredDispatched] = useState(false);

  /**
   * Handle IdP token expiration by dispatching an event instead of immediate redirect.
   * This allows SessionTimeoutProvider to show a warning modal before logout,
   * giving users time to save their work.
   *
   * The event is caught by SessionTimeoutProvider which shows a 30-second countdown
   * modal before performing a hard logout.
   */
  const handleIdpTokenExpired = (error?: Error) => {
    // Prevent multiple dispatches from concurrent API calls failing
    if (idpExpiredDispatched) {
      logger.info("IdP expiration already dispatched, skipping duplicate");

      return;
    }

    setIdpExpiredDispatched(true);

    const authConfig = getAuthConfig();

    logger.info("Dispatching IdP session expired event", {
      provider: authConfig.provider,
      errorCode: (error as any)?.errorCode,
    });

    dispatchIdpSessionExpired({
      provider: authConfig.provider,
      errorCode: (error as any)?.errorCode,
      message: error?.message || "Token refresh failed",
      timestamp: Date.now(),
    });
  };

  useEffect(() => {
    const fetchAccessToken = async () => {
      // Don't try to fetch token if not authenticated or still loading
      if (!isAuthenticated || authLoading || !getAccessTokenSilently) {
        return;
      }

      // Don't try to fetch token if we've already dispatched an IdP expiration event
      if (idpExpiredDispatched) {
        logger.info("IdP expiration already dispatched, skipping token fetch");

        return;
      }

      logger.info("getAccessTokenSilently");
      try {
        setTokenError(null);
        const token = await getAccessTokenSilently();

        if (token) {
          setAccessToken(token);
          // Clear any global error banners once we have a valid token again
          window.dispatchEvent(new Event("app:clear-errors"));
        } else {
          setTokenError("No access token received");
          logger.info("No access token received");
        }
      } catch (error: unknown) {
        logger.error("Failed to get access token", error instanceof Error ? error : undefined);
        // Dispatch IdP expiration event - this triggers logout flow
        handleIdpTokenExpired(error instanceof Error ? error : undefined);
      }
    };

    fetchAccessToken();
  }, [getAccessTokenSilently, isAuthenticated, authLoading, idpExpiredDispatched]);

  const client = useMemo(() => {
    if (!accessToken) {
      return {
        platformClient: null,
        adminClient: null,
        dataHubClient: null,
        sseWorkflowClient: () => null,
        authenticatedFetch: null,
      };
    }

    try {
      const platformClient = new WebuildAiPlatform({
        auth: async () => {
          try {
            if (getAccessTokenSilently) {
              const token = await getAccessTokenSilently();

              if (typeof token === "string") {
                return token;
              }
              throw new Error("No access token received");
            }
            throw new Error("getAccessTokenSilently is not available");
          } catch (error) {
            logger.error(
              "🚨 Platform client auth failed",
              error instanceof Error ? error : undefined,
              {
                status: (error as any)?.status,
                message: (error as any)?.message,
              },
            );
            // Dispatch IdP expiration event to show warning modal
            handleIdpTokenExpired(error instanceof Error ? error : undefined);
            throw error;
          }
        },
        serverURL: siteConfig().platformAPIURL,
      });

      const adminClient = new WeBuildAiAdmin({
        auth: async () => {
          try {
            if (getAccessTokenSilently) {
              const token = await getAccessTokenSilently();

              if (typeof token === "string") {
                return token;
              }
              throw new Error("No access token received");
            }
            throw new Error("getAccessTokenSilently is not available");
          } catch (error) {
            logger.error(
              "🚨 Admin client auth failed",
              error instanceof Error ? error : undefined,
              {
                status: (error as any)?.status,
                message: (error as any)?.message,
              },
            );
            // Dispatch IdP expiration event to show warning modal
            handleIdpTokenExpired(error instanceof Error ? error : undefined);
            throw error;
          }
        },
        serverURL: siteConfig().adminAPIURL,
      });

      const dataHubClient = new WebuildDatahub({
        auth: async () => {
          try {
            if (getAccessTokenSilently) {
              const token = await getAccessTokenSilently();

              if (typeof token === "string") {
                return token;
              }
              throw new Error("No access token received");
            }
            throw new Error("getAccessTokenSilently is not available");
          } catch (error) {
            logger.error(
              "🚨 DataHub client auth failed",
              error instanceof Error ? error : undefined,
              {
                status: (error as any)?.status,
                message: (error as any)?.message,
              },
            );
            // Dispatch IdP expiration event to show warning modal
            handleIdpTokenExpired(error instanceof Error ? error : undefined);
            throw error;
          }
        },
        serverURL: siteConfig().dataHubAPIURL,
      });

      const sseWorkflowClient = (workflowId: string) => {
        try {
          const es = new EventSource(
            `${platformClient._baseURL}platform/v1/workflow/${workflowId}/messages`,
            {
              fetch: async (input, init) => {
                try {
                  if (!getAccessTokenSilently) {
                    throw new Error("getAccessTokenSilently is not available");
                  }

                  // Always acquire a fresh token - never use stale cached tokens
                  // This ensures we respect IdP session policies (e.g., Azure AD admin revocation)
                  const token = await getAccessTokenSilently();

                  if (!token) {
                    throw new Error("No access token available for SSE");
                  }

                  return fetch(input, {
                    ...init,
                    headers: {
                      ...(init?.headers || {}),
                      Authorization: `Bearer ${token}`,
                      Accept: "text/event-stream",
                      "Cache-Control": "no-cache",
                    },
                  });
                } catch (err) {
                  logger.error(
                    "SSE token acquisition failed",
                    err instanceof Error ? err : undefined,
                  );
                  // Dispatch IdP expiration event to show warning modal
                  // This respects Azure AD/Auth0 session policies
                  handleIdpTokenExpired(err instanceof Error ? err : undefined);
                  throw err;
                }
              },
            },
          );

          // Add event listeners for debugging
          es.onopen = () => {
            logger.info("✅ SSE connection opened successfully");
          };

          es.onerror = (error) => {
            // Only log actual errors, not normal disconnections
            if (es.readyState === EventSource.CONNECTING) {
              logger.error("❌ SSE connection failed", error instanceof Error ? error : undefined);
            } else if (es.readyState === EventSource.CLOSED) {
              logger.info("🔌 SSE connection closed (normal after stream completes)");
            }
          };

          return es;
        } catch (error) {
          logger.error("Failed to create SSE client", error instanceof Error ? error : undefined);

          return null;
        }
      };

      // Authenticated fetch function for making API requests with auth headers
      const authenticatedFetch: AuthenticatedFetchFn = async (input, init) => {
        try {
          if (!getAccessTokenSilently) {
            throw new Error("getAccessTokenSilently is not available");
          }

          // Always acquire a fresh token - never use stale cached tokens
          // This ensures we respect IdP session policies (e.g., Azure AD admin revocation)
          const token = await getAccessTokenSilently();

          if (!token) {
            throw new Error("No access token available for fetch");
          }

          return fetch(input, {
            ...init,
            headers: {
              ...(init?.headers || {}),
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (err) {
          logger.error("Authenticated fetch failed", err instanceof Error ? err : undefined);
          // Dispatch IdP expiration event to show warning modal
          // This respects Azure AD/Auth0 session policies
          handleIdpTokenExpired(err instanceof Error ? err : undefined);
          throw err;
        }
      };

      return { platformClient, adminClient, dataHubClient, sseWorkflowClient, authenticatedFetch };
    } catch (error) {
      logger.error("Failed to create API clients", error instanceof Error ? error : undefined);

      return {
        platformClient: null,
        adminClient: null,
        dataHubClient: null,
        sseWorkflowClient: () => null,
        authenticatedFetch: null,
      };
    }
  }, [accessToken]);

  return (
    <WebuildAPIClientContext.Provider value={client}>{children}</WebuildAPIClientContext.Provider>
  );
};

export const useWebuildAPIClient = () => useContext(WebuildAPIClientContext);
